import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MapPinIcon } from "@heroicons/react/24/outline"
import { TextareaField } from "./TextareaField"
import { buildLocationUrl } from "../utils/maps"
import { extractMapsLink } from "../utils/incidentDetailsValidation"

const GEOLOCATION_ERRORS = { 1: 'PERMISSION_DENIED', 2: 'POSITION_UNAVAILABLE', 3: 'TIMEOUT' }
const GEOLOCATION_TIMEOUT_MS = 10000
// The browser's own timeout only starts once permission is granted, and a dismissed prompt can
// leave both callbacks unfired, so the button gives up on its own after this.
const GEOLOCATION_GIVE_UP_MS = 15000
// A pin is only worth sending if it is precise: phone GPS reports 5-65 m and city Wi-Fi 20-100 m,
// while "approximate location" (e.g. iOS with Precise Location off) or an IP guess is 1 km and
// more. A rejected fix leaves the field empty, and platform then locates the typed address.
const MAX_ACCURACY_METERS = 150

// A Google Maps link input the buyer can paste into, or fill from the device's current position.
// It has no maxLength on purpose: the browser would silently cut a long pasted link into a
// broken one that still looks valid; the form shows the too-long error instead.
export const LocationField = ({
    label,
    description,
    name,
    value = "",
    onChange = () => {},
    errorMessage = null
}) => {
    const { i18n } = useTranslation()
    const [isLocating, setIsLocating] = useState(false)
    const [geolocationError, setGeolocationError] = useState(null)
    const canGeolocate = typeof navigator !== 'undefined' && !!navigator.geolocation

    // A lookup only fills the field if nothing changed it meanwhile and it is still on screen.
    const valueRef = useRef(value)
    valueRef.current = value
    const requestRef = useRef(0)
    const giveUpRef = useRef(null)
    useEffect(() => () => {
        requestRef.current += 1
        clearTimeout(giveUpRef.current)
    }, [])

    const handleChange = (input) => {
        setGeolocationError(null)
        onChange(extractMapsLink(input))
    }

    const fillFromCurrentLocation = () => {
        const request = ++requestRef.current
        const valueAtClick = valueRef.current
        const settle = (handler) => (...args) => {
            if (request !== requestRef.current) return
            requestRef.current += 1
            clearTimeout(giveUpRef.current)
            setIsLocating(false)
            handler(...args)
        }
        const onSuccess = settle(position => {
            if (valueRef.current !== valueAtClick) return
            const { latitude, longitude, accuracy } = position.coords
            if (accuracy > MAX_ACCURACY_METERS) return setGeolocationError('INACCURATE')
            const url = buildLocationUrl(latitude, longitude)
            if (url) onChange(url)
            else setGeolocationError('POSITION_UNAVAILABLE')
        })
        const onFailure = settle(error => setGeolocationError(GEOLOCATION_ERRORS[error?.code] || 'POSITION_UNAVAILABLE'))

        setGeolocationError(null)
        setIsLocating(true)
        clearTimeout(giveUpRef.current)
        giveUpRef.current = setTimeout(() => onFailure({ code: 3 }), GEOLOCATION_GIVE_UP_MS)
        navigator.geolocation.getCurrentPosition(onSuccess, onFailure,
            { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 0 })
    }

    return (
        <>
            <TextareaField
                label={label}
                description={description}
                name={name}
                rows={1}
                initialValue={value}
                value={value}
                onChange={handleChange}
                maxLength={null}
                className={`resize-none ${errorMessage ? "border-red-500" : ""}`}
            />
            {errorMessage && <p className="text-red-500 text-sm ml-1 mt-1">{errorMessage}</p>}
            {canGeolocate &&
                <button
                    type="button"
                    className="self-start flex items-center gap-1 mt-2 ml-1 text-sm text-blue-600 hover:underline disabled:opacity-50"
                    onClick={fillFromCurrentLocation}
                    disabled={isLocating}
                >
                    <MapPinIcon className="h-4 w-4" aria-hidden="true" />
                    {isLocating
                        ? i18n.t('reportIncident.form.location.locating')
                        : i18n.t('reportIncident.form.location.use_current_location')}
                </button>
            }
            {geolocationError &&
                <p className="text-red-500 text-sm ml-1 mt-1">{i18n.t(`reportIncident.form.location.errors.${geolocationError}`)}</p>}
        </>
    )
}
